// src/layout/builders/ProjectRejection/RejectionTable.jsx
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import useProjectStatus from "../../../store/builders/useProjectStatus";
import ProjectTable from "../ProjectTable"; // Assuming ProjectTable handles display now
import { AiOutlineInfoCircle } from "react-icons/ai"; // For info icon
import { motion, AnimatePresence } from "framer-motion"; // For modal
import { X } from "lucide-react"; // For close button

const RejectionTable = () => {
  const navigate = useNavigate();
  const { projects, isLoading, fetchProjects, error } = useProjectStatus();
  const [showReasonModal, setShowReasonModal] = useState(false);
  const [selectedProjectReason, setSelectedProjectReason] = useState(null);

  useEffect(() => {
    fetchProjects("rejected");
  }, [fetchProjects]);

  // No status changes allowed from this view
  const handleStatusChange = async (projectId, newStatus, comment = null) => {
    console.warn("Cannot change status from Rejected Projects view.");
  };

   const handleViewReason = (project) => {
      setSelectedProjectReason(project);
      setShowReasonModal(true);
   };

   const handleCloseReasonModal = () => {
      setShowReasonModal(false);
      setSelectedProjectReason(null);
   };

  if (isLoading && !projects.length) { // Improved loading state check
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
      return <div className="text-center py-6 text-red-500 text-sm">{error}</div>;
  }

  return (
    <>
      <ProjectTable
        title="Rejected Projects"
        projects={projects}
        handleStatusChange={handleStatusChange}
        navigate={navigate}
        allowedNextStatuses={[]}
        showActionsColumn={true} // Keep actions column to add "View Reason"
        extraActions={(project) => ( // Use extraActions to add the button
          project.rejection_reason ? (
            <button
              onClick={() => handleViewReason(project)}
              title="View Rejection Details"
              className="px-2 py-1 text-[10px] bg-red-100 text-red-600 rounded hover:bg-red-200 transition-colors duration-200 flex items-center justify-center gap-1"
            >
              <AiOutlineInfoCircle className="w-3 h-3 flex-shrink-0" /> Reason
            </button>
          ) : null // Don't show button if no reason (e.g., invitation rejection)
        )}
      />
       {!isLoading && projects.length === 0 && (
         <div className="text-center py-10 text-gray-500 text-sm bg-white rounded-lg shadow mt-4">
           No projects have been rejected.
         </div>
       )}

        {/* Rejection Reason Modal */}
        <AnimatePresence>
            {showReasonModal && selectedProjectReason && (
               <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4"
                  onClick={handleCloseReasonModal}
               >
                  <motion.div
                     initial={{ scale: 0.9, opacity: 0 }}
                     animate={{ scale: 1, opacity: 1 }}
                     exit={{ scale: 0.9, opacity: 0 }}
                     transition={{ type: "spring", stiffness: 300, damping: 20 }}
                     className="bg-white rounded-lg p-5 max-w-md w-full shadow-xl relative"
                     onClick={(e) => e.stopPropagation()} // Prevent closing on inner click
                  >
                     <button
                        onClick={handleCloseReasonModal}
                        className="absolute top-2 right-2 text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-full hover:bg-gray-100"
                        aria-label="Close rejection reason popup"
                     >
                        <X className="w-5 h-5" />
                     </button>
                     <h3 className="text-base font-semibold text-gray-800 mb-3 flex items-center gap-2">
                        <AiOutlineInfoCircle className="w-5 h-5 text-red-500" /> Rejection Details
                     </h3>
                      <div className="mb-3 text-xs bg-gray-50 p-2 rounded border border-gray-100">
                         <p><span className="font-medium text-gray-600">Project:</span> {selectedProjectReason.project_name || 'N/A'}</p>
                         <p><span className="font-medium text-gray-600">Customer:</span> {selectedProjectReason.customer_name || 'N/A'} ({selectedProjectReason.customer_ducktail_id || 'N/A'})</p>
                      </div>
                       <p className="text-xs text-gray-500 font-medium mb-1">Rejection Type:</p>
                      <p className="text-sm text-gray-700 mb-3 capitalize font-medium px-2 py-1 inline-block rounded"
                         style={{
                           backgroundColor: selectedProjectReason.rejection_type === 'invitation' ? '#fffbeb' : '#fee2e2', // Light yellow vs light red
                           color: selectedProjectReason.rejection_type === 'invitation' ? '#b45309' : '#b91c1c' // Amber vs Red text
                         }}
                      >
                         {selectedProjectReason.rejection_type === 'invitation' ? 'Invitation Declined' : 'Rejected After Start'}
                      </p>
                      <p className="text-xs text-gray-500 font-medium mb-1">Reason:</p>
                     <p className="text-sm text-gray-700 break-words whitespace-pre-wrap">
                        {selectedProjectReason.rejection_reason || (selectedProjectReason.rejection_type === 'invitation' ? 'Reason not mandatory for invitation decline.' : 'No reason provided.')}
                     </p>
                     <div className="mt-4 flex justify-end">
                        <button
                           onClick={handleCloseReasonModal}
                           className="px-4 py-1.5 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors duration-200 text-xs font-medium"
                        >
                           Close
                        </button>
                     </div>
                  </motion.div>
               </motion.div>
            )}
        </AnimatePresence>
    </>
  );
};

export default RejectionTable;